import { useMemo } from 'react';

import { useSlideCheckout } from '@zenbusiness/application-commons-ecommerce';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import Tooltip from '@zenbusiness/zui/Tooltip';

import useCartUrl from '#client/hooks/useCartUrl';
import useCheckoutComplete from '#client/hooks/useCheckoutComplete';
import { useProductLandingContext } from '#client/pages/ProductLanding/contexts/useProductLanding';
import { useJurisdictions } from '#client/pages/ProductLanding/hooks/useJurisdictions';

import { useBuyBoxContext } from './BuyBoxContext';

const CTAButton = () => {
  const { isFederated } = useProductLandingContext();
  const {
    ctaIcon,
    ctaProps,
    ctaType,
    interval,
    openInNewTab,
    serviceCode,
    serviceToAddToCart
  } = useBuyBoxContext();

  const {
    ctaDisabledTooltip,
    isBuyEnabled = true,
    ctaText: label,
    allowsReviewPurchased = false
  } = ctaProps;

  const { palette } = useTheme();
  const buildCartUrl = useCartUrl();
  const { get } = usePriceList();
  const { formatMessage } = useIntl();
  const { openCheckout, closeCheckout } = useSlideCheckout();
  const { handleCheckoutComplete, getCheckoutCompleteUrl } = useCheckoutComplete();
  const checkoutCompleteUrl = getCheckoutCompleteUrl(serviceCode);
  const {
    jurisdictions,
    loading: jurisdictionsLoading,
    error: jurisdictionsError
  } = useJurisdictions(serviceCode);

  const servicesToAddToCart = useMemo(() => {
    if (!serviceToAddToCart) return [];
    return [{
      ...serviceToAddToCart,
      jurisdictions
    }];
  }, [serviceToAddToCart, jurisdictions]);

  const isInternalCheckout = ctaType === 'internalCheckout';
  const isJurisdictionsDisabled = jurisdictionsLoading || !!jurisdictionsError;

  const { clickEventProps, ctaUrl } = useMemo(() => {
    if (!serviceCode) {
      return {
        clickEventProps: {
          elementName: 'product_link_pressed',
          elementText: 'cta button'
        },
        ctaUrl: ''
      };
    }

    if (isInternalCheckout) {
      return {
        clickEventProps: {
          elementName: 'product_link_pressed',
          elementText: 'cta button',
          elementValue: serviceCode
        },
        ctaUrl: ''
      };
    }

    const url = buildCartUrl(serviceCode);

    const clickProps = {
      destination: url,
      elementName: 'product_link_pressed',
      elementText: 'cta button',
      elementValue: serviceCode
    };

    return {
      clickEventProps: clickProps,
      ctaUrl: url
    };
  }, [serviceCode, isInternalCheckout, buildCartUrl]);

  const getCheckoutParams = () => {
    // Always use servicesToAddToCart when available
    if (servicesToAddToCart.length > 0) {
      return { servicesToAddToCart };
    }

    const checkoutParams = { jurisdictions };

    // Interval can be not specified, in that case we use it as a service name
    if (!interval) {
      return { ...checkoutParams, serviceNames: [serviceCode] };
    }

    // If interval is specified, we use it to get the service pricing uuids
    // to avoid plans duplication that have same SKU for monthly and annual plans
    const priceListItems = get([serviceCode], {
      interval
    });

    if (!priceListItems || !priceListItems.length) {
      return { ...checkoutParams, serviceNames: [serviceCode] };
    }

    const cartServices = {
      servicePricingUuids: priceListItems
        .map(({ pricing }) => pricing.servicePricingUuid)
        .flat()
        .filter(Boolean)
    };

    return { ...checkoutParams, ...cartServices };
  };

  const onClickWithNavigate = () => {
    if (isInternalCheckout && !allowsReviewPurchased && !isJurisdictionsDisabled) {
      const checkoutParams = getCheckoutParams();

      openCheckout({
        ...checkoutParams,
        onClose: ({ isPurchaseComplete, orderUuid }) => {
          closeCheckout();
          if (isPurchaseComplete) {
            handleCheckoutComplete(serviceCode, orderUuid);
          }
        }
      });
    }
  };

  if (!serviceCode) {
    return (
      <Button>
        <FormattedMessage id={label} />
      </Button>
    );
  }

  // Tooltip in separate element since disabled buttons don't trigger hover events
  if ((!isBuyEnabled && ctaDisabledTooltip) || jurisdictionsError) {
    return (
      <Box position="relative" width="100%">
        <Button
          disabled
          fullWidth
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormattedMessage id={label} />
            <Tooltip
              arrow={false}
              componentsProps={{
                tooltip: {
                  sx: {
                    width: '13rem',
                    borderRadius: '0.25rem',
                    padding: '0.5rem'
                  }
                }
              }}
              enterTouchDelay={0}
              leaveTouchDelay={15000}
              title={(
                <Text size="xs" weight="regular">
                  <FormattedMessage id={ctaDisabledTooltip} />
                </Text>
              )}
              placement="top-start"
            >
              <Box sx={{ pointerEvents: 'auto' }}>
                <SharedAsset
                  alt=""
                  color={palette.info.light}
                  src="/s2/material-icons/info.svg"
                  width={20}
                />
              </Box>
            </Tooltip>
          </Stack>
        </Button>
      </Box>
    );
  }

  return (
    <Button
      clickstreamProps={clickEventProps}
      fullWidth
      {...(!isInternalCheckout && { href: ctaUrl })}
      {...(allowsReviewPurchased && { href: checkoutCompleteUrl })}
      {...(isInternalCheckout && { onClick: onClickWithNavigate })}
      {...((openInNewTab || (isFederated && !isInternalCheckout)) && { target: '_blank' })}
      {...(ctaIcon && ctaIcon.src && {
        icon: (
          <SharedAsset
            alt={ctaIcon.alt ? formatMessage({ id: ctaIcon.alt }) : ''}
            src={ctaIcon.src}
            color={palette.text.white}
            width={28}
          />
        ),
        iconPosition: 'end'
      })}
      loading={jurisdictionsLoading}
    >
      <FormattedMessage id={label} />
    </Button>
  );
};

export default CTAButton;
